import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles } from 'lucide-react';
import type { CivicLevel, Badge } from '../lib/civicLevelTypes';
import { getLevelDescription } from '../lib/civicLevelService';

interface LevelUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  newLevel: CivicLevel;
  badge: Badge | null;
}

export default function LevelUpModal({
  isOpen,
  onClose,
  newLevel,
  badge,
}: LevelUpModalProps) {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowContent(false);
      return;
    }

    setTimeout(() => setShowContent(true), 100);

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const description = getLevelDescription(newLevel);

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        background: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(4px)',
        padding: 'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom)) 16px',
      }}
    >
      <div
        className={`relative w-full bg-[#141414] rounded-2xl p-6 md:p-8 border transform transition-all duration-500 ${
          showContent ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
        }`}
        style={{
          maxWidth: 'min(92vw, 448px)',
          maxHeight: 'min(90vh, calc(100vh - 32px))',
          borderColor: 'rgba(64, 224, 208, 0.35)',
          overflow: 'auto',
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-[#808080] hover:text-[#A0A0A0]" />
        </button>

        <div className="text-center space-y-6">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border"
            style={{
              backgroundColor: 'rgba(64, 224, 208, 0.12)',
              borderColor: 'rgba(64, 224, 208, 0.35)',
            }}
          >
            <Sparkles className="w-4 h-4 animate-pulse" style={{ color: '#40E0D0' }} />
            <span className="text-[13px] font-medium" style={{ color: '#40E0D0' }}>Level Up!</span>
          </div>

          <div className="relative">
            <div className="absolute inset-0 blur-3xl animate-pulse" style={{ backgroundColor: 'rgba(64, 224, 208, 0.12)' }}></div>

            {badge && (
              <div className="relative">
                <div
                  className="w-28 h-28 mx-auto rounded-full flex items-center justify-center text-5xl transform transition-transform duration-700"
                  style={{
                    background: `linear-gradient(135deg, ${badge.color_primary}, ${badge.color_secondary})`,
                  }}
                >
                  {badge.icon_symbol}
                </div>

                <div className="absolute -inset-4 rounded-full blur-2xl -z-10 animate-pulse" style={{ backgroundColor: 'rgba(64, 224, 208, 0.25)' }}></div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <h2 className="text-[24px] font-semibold text-[#F5F5F5]">
              You've Reached {newLevel}
            </h2>
          </div>

          <p className="text-[15px] text-[#CCCCCC] leading-relaxed px-4" style={{ lineHeight: '1.7' }}>
            {description}
          </p>

          <div className="pt-4">
            <button
              onClick={onClose}
              className="w-full py-4 px-6 font-semibold rounded-full transition-all"
              style={{
                backgroundColor: '#40E0D0',
                color: '#0A1414',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(64, 224, 208, 0.90)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#40E0D0';
              }}
            >
              Keep Going
            </button>
          </div>
        </div>

        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-ping"
              style={{
                backgroundColor: '#40E0D0',
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
